import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Tag, Button, Space, Tooltip,
  Typography, Drawer, Descriptions, Divider,
  Popconfirm,
} from 'antd';
import {
  ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined,
  CreditCardOutlined, FilePdfOutlined, DownloadOutlined,
  FileTextOutlined, BankOutlined, SafetyCertificateOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import KpiCards from '../../components/KpiCards';
import {
  listerToutes, valider, rejeter, confirmerPaiement,
  activerQuota, annuler, telechargerFacture, telechargerProforma,
  STATUTS_COMMANDE_DIGITALE,
} from '../../services/commandeAttestationDigitaleService';
import PageHeader from '../../components/PageHeader';
import RejeterCommandeModal from './modals/RejeterCommandeModal';
import EnregistrerChequeModal from './modals/EnregistrerChequeModal';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';

const { Text } = Typography;

const fmt = (v) => v != null ? Number(v).toLocaleString(localeIntl()) : '-';
const fmtDate = (v) => v ? new Date(v).toLocaleDateString(localeIntl()) : '-';
const fmtDateTime = (v) => v ? new Date(v).toLocaleString(localeIntl()) : '-';

async function downloadBlob(fetchFn, commandeId, filename) {
  const blob = await fetchFn(commandeId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Drawer détail + actions BNCB ──────────────────────────────────────────────
function DetailDrawer({ record, open, onClose, onAction }) {
  const { t } = useTranslation('attestationsDigitales');
  if (!record) return null;

  const statut = STATUTS_COMMANDE_DIGITALE[record.statut] || { label: record.statut, color: 'default' };

  const handleDownloadFacture = async () => {
    try {
      await downloadBlob(telechargerFacture, record.commandeTrackingId,
        `Facture_${record.numeroFacture || record.numeroCommande}.pdf`);
    } catch { toast.error(t('drawer.erreurFacture')); }
  };

  const handleDownloadProforma = async () => {
    try {
      await downloadBlob(telechargerProforma, record.commandeTrackingId,
        `Proforma_${record.numeroProforma || record.numeroCommande}.pdf`);
    } catch { toast.error(t('drawer.erreurProforma')); }
  };

  // Boutons d'action selon le statut
  const actions = [];

  if (record.numeroProforma) {
    actions.push(
      <Button key="proforma" icon={<FileTextOutlined />} onClick={handleDownloadProforma}>
        {t('drawer.proforma')}
      </Button>
    );
  }

  if (record.numeroFacture) {
    actions.push(
      <Button key="facture" icon={<FilePdfOutlined />} onClick={handleDownloadFacture}
        style={{ color: '#7B3219', borderColor: '#7B3219' }}>
        {t('drawer.factureTva')}
      </Button>
    );
  }

  if (record.statut === 'SOUMISE') {
    actions.push(
      <Popconfirm key="valider"
        title={t('drawer.validerTitre', { compagnie: record.compagnieRaisonSociale })}
        description={t('drawer.validerDescription', { quantite: fmt(record.quantite) })}
        onConfirm={() => onAction('valider', record)}
        okText={t('actions.valider')} cancelText={t('actions.annuler')}
        okButtonProps={{ style: { background: '#16a34a' } }}
      >
        <Button type="primary" icon={<CheckCircleOutlined />}
          style={{ background: '#16a34a', borderColor: '#16a34a' }}>
          {t('drawer.validerEmettre')}
        </Button>
      </Popconfirm>,
      <Button key="rejeter" danger icon={<CloseCircleOutlined />}
        onClick={() => onAction('rejeter', record)}>
        {t('actions.rejeter')}
      </Button>
    );
  }

  if (record.statut === 'EN_ATTENTE_PAIEMENT') {
    actions.push(
      <Button key="cheque" type="primary" icon={<BankOutlined />}
        onClick={() => onAction('enregistrerCheque', record)}
        style={{ background: '#1677ff', borderColor: '#1677ff' }}>
        {t('actions.enregistrerCheque')}
      </Button>
    );
  }

  if (record.statut === 'CHEQUE_RECU') {
    actions.push(
      <Popconfirm key="confirmer"
        title={t('drawer.confirmerEncaissementTitre')}
        description={t('drawer.confirmerEncaissementDescription')}
        onConfirm={() => onAction('confirmerPaiement', record)}
        okText={t('actions.confirmer')} cancelText={t('actions.annuler')}
      >
        <Button type="primary" icon={<CheckCircleOutlined />}
          style={{ background: '#0891b2', borderColor: '#0891b2' }}>
          {t('actions.confirmerEncaissement')}
        </Button>
      </Popconfirm>
    );
  }

  if (record.statut === 'PAYEE') {
    actions.push(
      <Popconfirm key="quota"
        title={t('drawer.activerQuotaTitre', { quantite: fmt(record.quantite) })}
        description={t('drawer.activerQuotaDescription')}
        onConfirm={() => onAction('activerQuota', record)}
        okText={t('actions.activer')} cancelText={t('actions.annuler')}
        okButtonProps={{ style: { background: '#16a34a' } }}
      >
        <Button type="primary" icon={<SafetyCertificateOutlined />}
          style={{ background: '#16a34a', borderColor: '#16a34a' }}>
          {t('actions.activerQuota')}
        </Button>
      </Popconfirm>
    );
  }

  if (['BROUILLON', 'SOUMISE', 'EN_ATTENTE_PAIEMENT'].includes(record.statut)) {
    actions.push(
      <Popconfirm key="annuler"
        title={t('drawer.annulerTitre')}
        onConfirm={() => onAction('annuler', record)}
        okText={t('actions.oui')} cancelText={t('actions.non')}
        okButtonProps={{ danger: true }}
      >
        <Button danger>{t('actions.annuler')}</Button>
      </Popconfirm>
    );
  }

  return (
    <Drawer
      title={
        <Space>
          <span style={{ fontWeight: 700 }}>{t('drawer.titre', { numero: record.numeroCommande })}</span>
          <Tag color={statut.color}>{statut.label}</Tag>
        </Space>
      }
      open={open}
      onClose={onClose}
      width={620}
      footer={
        <Space wrap style={{ justifyContent: 'flex-end', width: '100%' }}>
          {actions}
        </Space>
      }
    >
      {/* Infos générales */}
      <Descriptions column={1} size="small" bordered labelStyle={{ fontWeight: 600, width: 190 }}>
        <Descriptions.Item label={t('drawer.numeroCommande')}>
          <code style={{ fontWeight: 700 }}>{record.numeroCommande}</code>
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.compagnie')}>
          <span style={{ fontWeight: 600 }}>{record.compagnieRaisonSociale}</span>
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.quantiteDemandee')}>
          {t('drawer.quantiteAttestations', { quantite: fmt(record.quantite) })}
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.soldeRestant')}>
          {['VALIDEE', 'ECOULEE', 'LIVREE'].includes(record.statut)
            ? <span style={{ color: record.soldeRestant > 0 ? '#16a34a' : '#ef4444', fontWeight: 600 }}>
                {t('drawer.soldeAtt', { solde: fmt(record.soldeRestant) })}
              </span>
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.demandeePar')}>{record.demandeePar || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('drawer.dateCreation')}>{fmtDateTime(record.createdAt)}</Descriptions.Item>
        {record.soumisePar && (
          <Descriptions.Item label={t('drawer.soumisePar')}>{record.soumisePar}</Descriptions.Item>
        )}
        {record.dateSoumission && (
          <Descriptions.Item label={t('drawer.dateSoumission')}>{fmtDateTime(record.dateSoumission)}</Descriptions.Item>
        )}
        {record.valideParLogin && (
          <Descriptions.Item label={t('drawer.valideePar')}>{record.valideParLogin}</Descriptions.Item>
        )}
        {record.dateValidation && (
          <Descriptions.Item label={t('drawer.dateValidation')}>{fmtDateTime(record.dateValidation)}</Descriptions.Item>
        )}
        {record.dateActivationQuota && (
          <Descriptions.Item label={t('drawer.quotaActiveLe')}>{fmtDateTime(record.dateActivationQuota)}</Descriptions.Item>
        )}
        {record.confirmeReceptionPar && (
          <Descriptions.Item label={t('drawer.receptionConfirmeePar')}>{record.confirmeReceptionPar}</Descriptions.Item>
        )}
        {record.commentaire && (
          <Descriptions.Item label={t('drawer.commentaire')}>{record.commentaire}</Descriptions.Item>
        )}
        {record.motifRejet && (
          <Descriptions.Item label={t('drawer.motifRejet')}>
            <span style={{ color: '#ef4444' }}>{record.motifRejet}</span>
          </Descriptions.Item>
        )}
      </Descriptions>

      {/* Proforma */}
      {record.numeroProforma && (
        <>
          <Divider style={{ margin: '20px 0 12px' }}>
            <Text style={{ color: '#ea580c', fontWeight: 600, fontSize: 13 }}>{t('drawer.proforma')}</Text>
          </Divider>
          <Descriptions column={1} size="small" bordered labelStyle={{ fontWeight: 600, width: 190 }}>
            <Descriptions.Item label={t('drawer.numeroProforma')}>
              <code style={{ fontWeight: 700, color: '#ea580c' }}>{record.numeroProforma}</code>
            </Descriptions.Item>
            <Descriptions.Item label={t('drawer.prixUnitaireHt')}>{fmt(record.prixUnitaire)} FCFA</Descriptions.Item>
            <Descriptions.Item label={t('drawer.montantHtEstime')}>
              <span style={{ fontWeight: 700 }}>{fmt(record.montantHt)} FCFA</span>
            </Descriptions.Item>
            {record.dateProforma && (
              <Descriptions.Item label={t('drawer.dateProforma')}>{fmtDate(record.dateProforma)}</Descriptions.Item>
            )}
          </Descriptions>
        </>
      )}

      {/* Facturation avec TVA */}
      {record.numeroFacture && (
        <>
          <Divider style={{ margin: '20px 0 12px' }}>
            <Text style={{ color: '#7B3219', fontWeight: 600, fontSize: 13 }}>{t('drawer.facturation')}</Text>
          </Divider>
          <Descriptions column={1} size="small" bordered labelStyle={{ fontWeight: 600, width: 190 }}>
            <Descriptions.Item label={t('drawer.numeroFacture')}>
              <code style={{ fontWeight: 700, color: '#7B3219' }}>{record.numeroFacture}</code>
            </Descriptions.Item>
            <Descriptions.Item label={t('drawer.montantHt')}>{fmt(record.montantHt)} FCFA</Descriptions.Item>
            {record.tauxTva != null && (
              <Descriptions.Item label={t('drawer.tvaTaux', { taux: fmt(record.tauxTva) })}>
                {fmt(record.montantTva)} FCFA
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('drawer.totalTtc')}>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#7B3219' }}>
                {fmt(record.montantTotal)} FCFA
              </span>
            </Descriptions.Item>
            <Descriptions.Item label={t('drawer.dateFacturation')}>{fmtDate(record.dateFacture)}</Descriptions.Item>
            {record.dateEcheanceFacture && (
              <Descriptions.Item label={t('drawer.echeance')}>
                <span style={{ color: '#d97706', fontWeight: 600 }}>
                  {fmtDate(record.dateEcheanceFacture)}
                </span>
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('drawer.factureePar')}>{record.facturePar || '-'}</Descriptions.Item>
          </Descriptions>
        </>
      )}

      {/* Paiement */}
      {record.numeroCheque && (
        <>
          <Divider style={{ margin: '20px 0 12px' }}>
            <Text style={{ color: '#059669', fontWeight: 600, fontSize: 13 }}>
              <CheckCircleOutlined style={{ marginRight: 6 }} />{t('drawer.paiementCheque')}
            </Text>
          </Divider>
          <Descriptions column={1} size="small" bordered labelStyle={{ fontWeight: 600, width: 190 }}>
            <Descriptions.Item label={t('drawer.numeroCheque')}>
              <code style={{ fontWeight: 700 }}>{record.numeroCheque}</code>
            </Descriptions.Item>
            <Descriptions.Item label={t('drawer.banqueEmettrice')}>{record.banqueEmettrice || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('drawer.montantCheque')}>
              <span style={{ fontWeight: 600 }}>{fmt(record.montantCheque)} FCFA</span>
            </Descriptions.Item>
            <Descriptions.Item label={t('drawer.dateEmission')}>{fmtDate(record.dateEmissionCheque)}</Descriptions.Item>
            <Descriptions.Item label={t('drawer.dateReception')}>{fmtDate(record.dateReceptionCheque)}</Descriptions.Item>
            <Descriptions.Item label={t('drawer.enregistrePar')}>{record.paiementEnregistrePar || '-'}</Descriptions.Item>
            {record.notesPaiement && (
              <Descriptions.Item label={t('drawer.notes')}>{record.notesPaiement}</Descriptions.Item>
            )}
          </Descriptions>
        </>
      )}
    </Drawer>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────
export default function CommandesAttestationsDigitalesPage() {
  const { t } = useTranslation('attestationsDigitales');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [commandeARejet, setCommandeARejet] = useState(null);
  const [commandeACheque, setCommandeACheque] = useState(null);

  const load = useCallback(async (page = 0, size = 20) => {
    setLoading(true);
    try {
      const result = await listerToutes(page, size);
      setData(result.items);
      setPagination((p) => ({ ...p, total: result.totalElements, current: page + 1 }));
    } catch {
      toast.error(i18n.t('attestationsDigitales:commandes.erreurChargement'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = () => load(pagination.current - 1, pagination.pageSize);

  const handleAction = async (action, record) => {
    if (action === 'rejeter') { setCommandeARejet(record); return; }
    if (action === 'enregistrerCheque') { setCommandeACheque(record); return; }

    setActionLoading(action + record.commandeTrackingId);
    try {
      if (action === 'valider') {
        await valider(record.commandeTrackingId);
        toast.success(t('commandes.messages.validee', { numero: record.numeroCommande }));
      } else if (action === 'confirmerPaiement') {
        await confirmerPaiement(record.commandeTrackingId);
        toast.success(t('commandes.messages.paiementConfirme', { numero: record.numeroCommande }));
      } else if (action === 'activerQuota') {
        await activerQuota(record.commandeTrackingId);
        toast.success(t('commandes.messages.quotaActive', { quantite: fmt(record.quantite), compagnie: record.compagnieRaisonSociale }));
      } else if (action === 'annuler') {
        await annuler(record.commandeTrackingId);
        toast.success(t('commandes.messages.annulee', { numero: record.numeroCommande }));
      }
      setDetailOpen(false);
      setSelected(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('commandes.messages.erreurOperation'));
    } finally {
      setActionLoading(null);
    }
  };

  // Stats dynamiques
  const stats = {
    aValider:   data.filter(c => c.statut === 'SOUMISE').length,
    enAttente:  data.filter(c => c.statut === 'EN_ATTENTE_PAIEMENT').length,
    chequesRecus: data.filter(c => c.statut === 'CHEQUE_RECU').length,
    aActiver:   data.filter(c => c.statut === 'PAYEE').length,
    actives:    data.filter(c => c.statut === 'VALIDEE').length,
  };

  const columns = [
    {
      title: t('commandes.colonnes.numero'),
      dataIndex: 'numeroCommande',
      key: 'numero',
      render: (v) => <code style={{ fontWeight: 600 }}>{v}</code>,
    },
    {
      title: t('commandes.colonnes.compagnie'),
      dataIndex: 'compagnieRaisonSociale',
      key: 'compagnie',
      ellipsis: true,
    },
    {
      title: t('commandes.colonnes.quantite'),
      dataIndex: 'quantite',
      key: 'quantite',
      align: 'right',
      render: (v) => fmt(v),
    },
    {
      title: t('commandes.colonnes.montantTtc'),
      dataIndex: 'montantTotal',
      key: 'montant',
      align: 'right',
      render: (v) => v ? <span style={{ fontWeight: 600 }}>{fmt(v)} <small>FCFA</small></span>
                       : <span style={{ color: '#aaa' }}>-</span>,
    },
    {
      title: t('commandes.colonnes.statut'),
      dataIndex: 'statut',
      key: 'statut',
      render: (s) => {
        const info = STATUTS_COMMANDE_DIGITALE[s] || { label: s, color: 'default' };
        return <Tag color={info.color}>{info.label}</Tag>;
      },
    },
    {
      title: t('commandes.colonnes.demandeePar'),
      dataIndex: 'demandeePar',
      key: 'demandeur',
      ellipsis: true,
    },
    {
      title: t('commandes.colonnes.date'),
      dataIndex: 'createdAt',
      key: 'date',
      render: (v) => fmtDate(v),
    },
    {
      title: t('commandes.colonnes.actionsRapides'),
      key: 'actions',
      fixed: 'right',
      width: 160,
      render: (_, record) => {
        const busy = actionLoading === record.commandeTrackingId;
        return (
          <Space size="small">
            {record.statut === 'SOUMISE' && (
              <Tooltip title={t('commandes.validerEmettre')}>
                <Popconfirm
                  title={t('commandes.validerTitre', { numero: record.numeroCommande })}
                  description={t('commandes.validerDescription', { quantite: fmt(record.quantite) })}
                  onConfirm={() => handleAction('valider', record)}
                  okText={t('actions.valider')} cancelText={t('actions.non')}
                  okButtonProps={{ style: { background: '#16a34a' } }}
                >
                  <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                    loading={busy} style={{ background: '#16a34a', borderColor: '#16a34a' }} />
                </Popconfirm>
              </Tooltip>
            )}
            {record.statut === 'SOUMISE' && (
              <Tooltip title={t('actions.rejeter')}>
                <Button size="small" danger icon={<CloseCircleOutlined />}
                  onClick={() => setCommandeARejet(record)} />
              </Tooltip>
            )}
            {record.statut === 'EN_ATTENTE_PAIEMENT' && (
              <Tooltip title={t('actions.enregistrerCheque')}>
                <Button size="small" type="primary" icon={<BankOutlined />}
                  onClick={() => setCommandeACheque(record)}
                  style={{ background: '#1677ff', borderColor: '#1677ff' }} />
              </Tooltip>
            )}
            {record.statut === 'CHEQUE_RECU' && (
              <Tooltip title={t('actions.confirmerEncaissement')}>
                <Popconfirm
                  title={t('commandes.chequeEncaisseTitre')}
                  onConfirm={() => handleAction('confirmerPaiement', record)}
                  okText={t('actions.oui')} cancelText={t('actions.non')}
                >
                  <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                    loading={busy} style={{ background: '#0891b2', borderColor: '#0891b2' }} />
                </Popconfirm>
              </Tooltip>
            )}
            {record.statut === 'PAYEE' && (
              <Tooltip title={t('actions.activerQuota')}>
                <Popconfirm
                  title={t('commandes.activerTitre', { quantite: fmt(record.quantite) })}
                  onConfirm={() => handleAction('activerQuota', record)}
                  okText={t('actions.activer')} cancelText={t('actions.non')}
                  okButtonProps={{ style: { background: '#16a34a' } }}
                >
                  <Button size="small" type="primary" icon={<SafetyCertificateOutlined />}
                    loading={busy} style={{ background: '#16a34a', borderColor: '#16a34a' }} />
                </Popconfirm>
              </Tooltip>
            )}
            <Tooltip title={t('commandes.voirDetail')}>
              <Button size="small" icon={<DownloadOutlined style={{ transform: 'rotate(0deg)' }} />}
                onClick={() => { setSelected(record); setDetailOpen(true); }}
                style={{ fontSize: 11 }}>
                {t('commandes.detail')}
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Layout>
      <div style={{ padding: '24px' }}>
        {/* `pagination.total` et non `data.length` : la pagination est serveur,
            `data` ne contient que la page courante. */}
        <PageHeader
          titre={t('commandes.titre')}
          icone={<FileTextOutlined />}
          compte={pagination.total}
          unite={t('commandes.unite')}
          unitePluriel={t('commandes.unitePluriel')}
          actions={<Button icon={<ReloadOutlined />} onClick={refresh}>{t('actions.actualiser')}</Button>}
        />

        {/* Indicateurs de statut - columns=5 explicite : le defaut plafonne a 4. */}
        <KpiCards
          columns={5}
          items={[
            { label: t('commandes.kpi.aValider'), value: stats.aValider, icon: <FileTextOutlined />, color: '#d97706', bg: '#fef3c7' },
            { label: t('commandes.kpi.enAttentePaiement'), value: stats.enAttente, icon: <ClockCircleOutlined />, color: '#f97316', bg: '#ffedd5' },
            { label: t('commandes.kpi.chequesRecus'), value: stats.chequesRecus, icon: <BankOutlined />, color: '#0891b2', bg: '#cffafe' },
            { label: t('commandes.kpi.aActiver'), value: stats.aActiver, icon: <SafetyCertificateOutlined />, color: '#2563eb', bg: '#dbeafe' },
            { label: t('commandes.kpi.quotasActifs'), value: stats.actives, icon: <CreditCardOutlined />, color: '#16a34a', bg: '#dcfce7' },
          ]}
          style={{ marginBottom: 24 }}
        />

        <Table
          columns={columns}
          dataSource={data}
          rowKey="commandeTrackingId"
          loading={loading}
          onRow={(rec) => ({
            onClick: () => { setSelected(rec); setDetailOpen(true); },
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page, size) => load(page - 1, size),
            showSizeChanger: true,
            showTotal: (total) => t('commandes.total', { n: total }),
          }}
          scroll={{ x: 1100 }}
          size="small"
          rowClassName={(rec) =>
            rec.statut === 'SOUMISE' ? 'row-highlight-warning'
            : rec.statut === 'EN_ATTENTE_PAIEMENT' ? 'row-highlight-orange'
            : rec.statut === 'PAYEE' ? 'row-highlight-blue'
            : ''
          }
        />
      </div>

      <DetailDrawer
        record={selected}
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSelected(null); }}
        onAction={handleAction}
      />

      <RejeterCommandeModal
        open={!!commandeARejet}
        commande={commandeARejet}
        onClose={() => setCommandeARejet(null)}
        onSuccess={() => { refresh(); setCommandeARejet(null); setDetailOpen(false); setSelected(null); }}
      />

      <EnregistrerChequeModal
        open={!!commandeACheque}
        commande={commandeACheque}
        onClose={() => setCommandeACheque(null)}
        onSuccess={() => { refresh(); setCommandeACheque(null); setDetailOpen(false); setSelected(null); }}
      />
    </Layout>
  );
}
